import { Alert, Button, Heading, Link, Text } from "@oysterhr/ui"
import ConnectPaymentMethodLayout from "../../../layouts/ConnectPaymentMethodLayout"
import { Trans, useTranslation } from "react-i18next"
import { OverviewStep } from "../../../components/OverviewStep"
import { useMemo } from "react"
import { useAccountAspect } from "@oysterhr/identity"
import { useNavigate } from "react-router-dom"
import { useCompanyPaymentsSettingsContext } from "../../../contexts/CompanyPaymentsSettingsContext"
import { PaymentEngagementType } from "../../../enums"
import { companyNewSepaFlowPath, companySepaFlowPath } from "../../../../../utils/routeHelpers"

interface SepaLandingPageProps {
  type: PaymentEngagementType
}

const HC_ARTICLE = "https://support.oysterhr.com/hc/en-us/articles/4405829333009"

const SETUP_STEPS = {
  fte: [
    {
      title: "fte.steps.addPaymentMethod.title",
      content: "fte.steps.addPaymentMethod.content",
    },
    {
      title: "fte.steps.authorization.title",
      content: "fte.steps.authorization.content",
    },
    {
      title: "fte.steps.signMandate.title",
      content: "fte.steps.signMandate.content",
    },
    {
      title: "fte.steps.mandateSharing.title",
      content: "fte.steps.mandateSharing.content",
    },
    {
      title: "fte.steps.verification.title",
      content: "fte.steps.verification.content",
    },
  ],
  contractor: [
    {
      title: "contractor.steps.addPaymentMethod.title",
      content: "contractor.steps.addPaymentMethod.content",
    },
    {
      title: "contractor.steps.authorization.title",
      content: "contractor.steps.authorization.content",
    },
  ],
}

const Steps = ({ type }: { type: SepaLandingPageProps["type"] }) => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/SepaLandingPage")

  if (type === PaymentEngagementType.Combined) return null
  const steps = SETUP_STEPS[type]

  return (
    <div className="mt-6">
      <Heading size="lg">{t("stepsHeader")}</Heading>
      <ul className="mt-2 grid grid-cols-1 justify-items-start gap-4 rounded-lg border-2 border-gray-200 p-4">
        {steps.map((step, index) => (
          <OverviewStep
            key={step.title}
            title={t(step.title)}
            content={t(step.content)}
            step={index + 1}
          />
        ))}
      </ul>
    </div>
  )
}
const SepaLandingPage = ({ type }: SepaLandingPageProps) => {
  const navigate = useNavigate()
  const { accountAspect: companyId } = useAccountAspect()
  const { contractorDirectDebitEnabled } = useCompanyPaymentsSettingsContext()
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/SepaLandingPage")
  const url = useMemo(() => {
    switch (type) {
      case PaymentEngagementType.Combined:
        return companySepaFlowPath(companyId)
      case PaymentEngagementType.Contractor:
        return companyNewSepaFlowPath(companyId, PaymentEngagementType.Contractor)
      case PaymentEngagementType.Fte:
        return companyNewSepaFlowPath(companyId, PaymentEngagementType.Fte)
    }
  }, [companyId, type])

  // This is just used to show different FAQ answers
  const resolvedType = useMemo(() => {
    if (type === PaymentEngagementType.Combined && !contractorDirectDebitEnabled) {
      return "fte"
    }
    return type
  }, [type, contractorDirectDebitEnabled])

  const handleContinue = () => {
    navigate(url)
  }

  return (
    <ConnectPaymentMethodLayout title={t("title")} subtitle={t("subtitle")}>
      <div className="relative w-[720px] rounded-lg border-2 border-gray-200 bg-white p-2 text-left md:p-8">
        <div className="flex flex-col gap-6">
          <div>
            <Text className="text-base font-semibold leading-6">
              {t("whatDoesSepaDo.question")}
            </Text>
            <Text size="medium" className="mt-2 text-base font-normal leading-6">
              {t("whatDoesSepaDo.answer")}
            </Text>
          </div>

          <div>
            <Text className="text-base font-semibold leading-6">
              {t("whichInvoicesWillBePaid.question")}
            </Text>
            <Text size="medium" className="mt-2 text-base font-normal leading-6">
              {t(`whichInvoicesWillBePaid.answer.${resolvedType}`)}
            </Text>
          </div>
        </div>
        <Alert variation="info" className="mt-2">
          <Alert.Icon />
          <Alert.Content>
            <Text size="medium" className="text-base font-normal leading-6">
              {t("alert")}
            </Text>
          </Alert.Content>
        </Alert>

        <Steps type={type} />

        <div className="my-6">
          <Trans
            t={t}
            i18nKey="details"
            components={{
              a: <Link to={HC_ARTICLE} className="inline-block font-normal" target="_blank" />,
            }}
          />
        </div>
        <Button variation="primary" size="md" onClick={handleContinue}>
          {t("continue")}
        </Button>
      </div>
    </ConnectPaymentMethodLayout>
  )
}

export default SepaLandingPage
